/**
 * The player's own words (buttons, labels, the end card's count), chosen by the demo document's
 * `locale`. English is the fallback for any language or key not covered. Plain objects, no
 * runtime dependency; the runtime picks the table once and hands it to what draws words (watch.js
 * parts get the few strings they need as arguments so the table is not bundled twice).
 */

export interface Strings {
  clickThrough: string;
  watch: string;
  pause: string;
  replay: string;
  madeWith: string;
  next: string;
  back: string;
  skip: string;
  prevStep: string;
  nextStep: string;
  finish: string;
  mute: string;
  unmute: string;
  volume: string;
  timeline: string;
  /** "Part 2" (a chapter card's eyebrow). */
  part(n: number): string;
  /** "Step 3" (a step with no name). */
  step(n: number): string;
  /** "Step 3: Name" (a scrub segment's label). */
  stepNamed(n: number, name: string): string;
  /** "Step 2 of 7" (the screen reader's announcement). */
  stepOf(i: number, n: number): string;
  /** "Highlight 2 of 3". */
  highlight(i: number, n: number): string;
  /** "Click Save". */
  click(label: string): string;
  /** "3 steps · done" (CSS uppercases it). */
  done(n: number): string;
  gateTitle: string;
  gateSub: string;
  gateContinue: string;
  gateSkip: string;
  privacy: string;
  sharedWith(to: string | null): string;
  fieldEmail: string;
  fieldName: string;
  fieldCompany: string;
  enterEmail: string;
  enterName: string;
  enterCompany: string;
  badEmail: string;
}

const en: Strings = {
  clickThrough: 'Click through',
  watch: 'Watch',
  pause: 'Pause',
  replay: 'Replay',
  madeWith: 'Made with mado',
  next: 'Next',
  back: 'Back',
  skip: 'Skip',
  prevStep: 'Previous step',
  nextStep: 'Next step',
  finish: 'Finish',
  mute: 'Mute',
  unmute: 'Unmute',
  volume: 'Volume',
  timeline: 'Timeline',
  part: (n) => `Part ${n}`,
  step: (n) => `Step ${n}`,
  stepNamed: (n, name) => `Step ${n}: ${name}`,
  stepOf: (i, n) => `Step ${i} of ${n}`,
  highlight: (i, n) => `Highlight ${i} of ${n}`,
  click: (label) => `Click ${label}`,
  done: (n) => `${n} ${n === 1 ? 'step' : 'steps'} · done`,
  gateTitle: 'See the rest of the demo',
  gateSub: 'Leave your details to carry on.',
  gateContinue: 'Continue',
  gateSkip: 'Skip',
  privacy: 'Privacy policy',
  sharedWith: (to) => `Shared with ${to || 'the owner of this demo'}.`,
  fieldEmail: 'Work email',
  fieldName: 'Name',
  fieldCompany: 'Company',
  enterEmail: 'Enter your work email.',
  enterName: 'Enter your name.',
  enterCompany: 'Enter your company.',
  badEmail: 'Enter an email address like name@company.com.',
};

/** Lithuanian: 1, 21, 31 take the singular; 10 and 11 to 19 (and 20, 30) the genitive plural; the rest the plural. */
export function ltStepWord(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'žingsnis';
  if (m10 === 0 || (m100 >= 11 && m100 <= 19)) return 'žingsnių';
  return 'žingsniai';
}

const lt: Strings = {
  clickThrough: 'Spaudinėti pačiam',
  watch: 'Žiūrėti',
  pause: 'Pauzė',
  replay: 'Žiūrėti iš naujo',
  madeWith: 'Sukurta su mado',
  next: 'Toliau',
  back: 'Atgal',
  skip: 'Praleisti',
  prevStep: 'Ankstesnis žingsnis',
  nextStep: 'Kitas žingsnis',
  finish: 'Baigti',
  mute: 'Išjungti garsą',
  unmute: 'Įjungti garsą',
  volume: 'Garsumas',
  timeline: 'Laiko juosta',
  part: (n) => `${n} dalis`,
  step: (n) => `${n} žingsnis`,
  stepNamed: (n, name) => `${n} žingsnis: ${name}`,
  stepOf: (i, n) => `${i} žingsnis iš ${n}`,
  highlight: (i, n) => `Akcentas ${i} iš ${n}`,
  click: (label) => `Spauskite: ${label}`,
  done: (n) => `${n} ${ltStepWord(n)} · baigta`,
  gateTitle: 'Pažiūrėkite likusią demo dalį',
  gateSub: 'Palikite kontaktus ir tęskite.',
  gateContinue: 'Tęsti',
  gateSkip: 'Praleisti',
  privacy: 'Privatumo politika',
  sharedWith: (to) => `Duomenis gaus ${to || 'šios demo savininkas'}.`,
  fieldEmail: 'Darbo el. paštas',
  fieldName: 'Vardas',
  fieldCompany: 'Įmonė',
  enterEmail: 'Įveskite darbo el. paštą.',
  enterName: 'Įveskite savo vardą.',
  enterCompany: 'Įveskite savo įmonę.',
  badEmail: 'Įveskite el. pašto adresą, pavyzdžiui, vardas@imone.lt.',
};

const TABLES: Record<string, Partial<Strings>> = { en, lt };

/** `lt-LT`, `lt_LT`, `LT` all give `lt`. */
export function baseLanguage(locale: string | undefined | null): string {
  return (locale ?? '').trim().toLowerCase().split(/[-_]/)[0] ?? '';
}

/** The table for a locale: its own words, English for any key (or language) it lacks. */
export function stringsFor(locale: string | undefined | null): Strings {
  const own = TABLES[baseLanguage(locale)];
  return own && own !== en ? { ...en, ...own } : en;
}
